import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useSettingsContext } from 'context/useSettingsContext';
import { ReactNode } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

type ActionItemProps = {
  icon: IconProp;
  title: ReactNode;
  subtitle: ReactNode;
  variant?: 'danger' | 'success';
  onClick: () => void;
};

function ActionItem({ icon, title, subtitle, variant, onClick }: Readonly<ActionItemProps>) {
  return (
    <Dropdown.Item
      as="button"
      type="button"
      className={`d-flex align-items-center gap-2 rounded-2 px-2 py-2 ${
        variant ? `text-${variant}` : ''
      }`}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={icon} fixedWidth className="fs-0 text-600" />
      <span className="d-flex flex-column align-items-start lh-sm">
        <span className="fs--1 fw-semi-bold">{title}</span>
        <span className="fs--2 text-500">{subtitle}</span>
      </span>
    </Dropdown.Item>
  );
}

type Props = {
  canDownload: boolean;
  canCopy: boolean;
  isDeactivated: boolean;
  onDownload: () => void;
  onCopyKey: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
};

export default function LicenceActionsDropdown({
  canDownload,
  canCopy,
  isDeactivated,
  onDownload,
  onCopyKey,
  onActivate,
  onDeactivate,
}: Readonly<Props>) {
  const { t } = useTranslation('licenses');
  const {
    config: { isRTL },
  } = useSettingsContext();

  return (
    <Dropdown
      className="font-sans-serif btn-reveal-trigger d-inline-block"

      align={isRTL ? 'start' : 'end'}
    >
      <Dropdown.Toggle
        variant="link"
        size="sm"
        data-boundary="viewport"
        className="text-600 btn-reveal btn-reveal-sm"
      >
        <FontAwesomeIcon icon={['fas', 'ellipsis-v']} className="fs--2" />
      </Dropdown.Toggle>
      <Dropdown.Menu
        className="border shadow rounded-3 p-2"
        style={{ minWidth: '14rem' ,zIndex:9999}}
        renderOnMount
        popperConfig={{ strategy: 'fixed' }}
      >
        {canDownload && (
          <ActionItem
            icon={['fas', 'download']}
            title={t('download_licence')}
            subtitle={t('download_licence_sub')}
            onClick={onDownload}
          />
        )}
        {canCopy && (
          <ActionItem
            icon={['fas', 'copy']}
            title={t('copy_licence_key')}
            subtitle={t('copy_licence_key_sub')}
            onClick={onCopyKey}
          />
        )}
        {(canDownload || canCopy) && <Dropdown.Divider className="my-1" />}
        {isDeactivated ? (
          <ActionItem
            icon={['fas', 'check']}
            title={t('activate_licence')}
            subtitle={t('activate_licence_sub')}
            variant="success"
            onClick={onActivate}
          />
        ) : (
          <ActionItem
            icon={['fas', 'ban']}
            title={t('deactivate_licence')}
            subtitle={t('deactivate_licence_sub')}
            variant="danger"
            onClick={onDeactivate}
          />
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}
